// src/pages/misc/onboarding/OnboardingResumePage.tsx
// [HANDOFF] THE RESUME (/onboarding/continuar - routed at WU-145's switch, P-5; WU-142 UNIT 8 RES3, docs/manifest/WU-142.md
//   SESSION 39 RECORD UNIT 8 THE DESIGN D-R7 to D-R9 and SESSION 48 RECORD RES3 THE BUILD). The app's login sends him here with
//   `resume`, his application's id (C-3). An id that is no application id: not found, the login its one button. This browser's
//   draft holding that id: his checkout, with everything he chose. Otherwise the resume (P6) is read once: a legacy application
//   at pending_payment shows the old view's pay button (WU-145 D3: its body, its code unpreviewed), one at pending is not
//   found (no auth user: the edge never checks it out) and any other legacy state is the return page's; a per-seat application
//   waiting for its payment shows the account as built - the server's lines, each named by the
//   checkout's own label, the period and the total - with the checkout's discount box (P3), his gold button (P4, back to the
//   return route) and the way back to change his order (the next submit rebuilds the unpaid account, WU-138 C-5); any other state
//   is the return page's to show. The state is P6's: P3 reports one and refuses none, and P4 refuses a paid application
//   (CHECKOUT_ALREADY_PAID: the return route). A refusal keeps the page: the rate limit shows the server's message, every other
//   refusal the page's own line, the read's with a retry. A screen he did not draw, in his style, inside his OnboardingFrame.
import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { useTranslation } from '../../../contexts/languages/LanguageContext';
import { publicJourney } from './continuousPath';
import OnboardingFrame from './OnboardingFrame';
import { LOGIN_URL, UUID_PATTERN } from './OnboardingReturnPage';
import {
  fetchPerSeatQuote,
  fetchPerSeatResume,
  startLegacyCheckout,
  startPerSeatCheckout,
  type ApiRefusal,
  type PerSeatQuote,
  type PerSeatQuoteDiscount,
  type PerSeatQuoteLine,
} from './perSeatApi';
import { EXTENSION_ROW, PLAN_IDS_BY_PRICE, PLAN_WORD, type ExtensionId } from './priceBook';
import { loadStructureDraft } from './structureDraft';
import { CYCLE_MONTHS, LINE_LABEL_KEYS, formatMxn, type BillingCycle } from '../../settings/create-organization/orderQuote';
import './builder.css';
import '../../settings/create-organization/orgFlow.css';
import '../../auth/auth.css';

const CHECKOUT_ROUTE = '/onboarding/checkout';
const RETURN_ROUTE = '/onboarding/estado';
const CHANGE_ROUTE = '/onboarding';
const CYCLES = Object.keys(CYCLE_MONTHS) as BillingCycle[];
const EXTENSION_IDS = Object.keys(EXTENSION_ROW) as ExtensionId[];

/** The account as built: P6's quote, P3's shape without a discount. */
type Account = Omit<PerSeatQuote, 'discount'>;

type View =
  | { kind: 'loading' }
  | { kind: 'account'; account: Account; cycle: BillingCycle }
  | { kind: 'returnPage' }
  | { kind: 'legacy' }
  | { kind: 'notFound' }
  | { kind: 'failed'; message?: string };

/** His billing cycle of the server's period (CYCLE_MONTHS read backwards); a period off the server's domain names none. */
function cycleOf(months: number): BillingCycle | undefined {
  return CYCLES.find((cycle) => CYCLE_MONTHS[cycle] === months);
}

/**
 * The old lane's view of its state (D3, self-stamp 19): at pending_payment its pay view; at pending - an application with
 * no auth user, which the edge never checks out (create-stripe-checkout.ts:354) - not found, as an id that is no application;
 * any other state the return page's, as the per-seat lane's.
 */
function legacyView(state: string): View {
  if (state === 'pending_payment') return { kind: 'legacy' };
  return state === 'pending' ? { kind: 'notFound' } : { kind: 'returnPage' };
}

/**
 * The checkout's own name of a line (SESSION 45 RECORD THE LINE LABELS FINDING, self-stamp 146; SESSION 48 RECORD, self-stamp
 * 154): the plan line by its plan's name, an extension line by its family and scope through the book's row map. A line no id
 * names keeps the server's row key - never dropped, so the lines shown always add up to the total (self-stamp 155).
 */
function lineLabel(line: PerSeatQuoteLine, plan: string, t: (key: string) => string): string {
  if (line.kind === 'plan') {
    const planId = PLAN_IDS_BY_PRICE.find((id) => PLAN_WORD[id] === plan);
    return planId === undefined ? line.rowKey : t(`settings.upgrade.plans.${planId}.name`);
  }
  const id = EXTENSION_IDS.find((x) => EXTENSION_ROW[x].family === line.family && EXTENSION_ROW[x].scope === line.scope);
  return id === undefined ? line.rowKey : t(LINE_LABEL_KEYS[id]);
}

/** The return page of an application the server will not check out: a paid one as Stripe's success URL carries it (D-C4). */
function returnRoute(applicationId: string, paid: boolean): string {
  return `${RETURN_ROUTE}?${paid ? 'status=success&' : ''}application_id=${encodeURIComponent(applicationId)}`;
}

const OnboardingResumePage: React.FC = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const given = params.get('resume') ?? '';
  const applicationId = UUID_PATTERN.test(given) ? given : null;
  // Read once: this browser's draft is C-3's same-browser clause (self-stamp 115) and the rising bar's place.
  const [draft] = useState(() => loadStructureDraft());
  const inThisBrowser = applicationId !== null && draft.applicationId === applicationId;
  const [view, setView] = useState<View>(applicationId === null ? { kind: 'notFound' } : { kind: 'loading' });
  const [reads, setReads] = useState(0);
  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState<PerSeatQuoteDiscount | null>(null);
  const [codeError, setCodeError] = useState('');
  const [failure, setFailure] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (applicationId === null || inThisBrowser) return undefined;
    let alive = true;
    setView({ kind: 'loading' });
    void fetchPerSeatResume(applicationId).then((result) => {
      if (!alive) return;
      if (!result.ok) {
        const { code: refused, message } = result.refusal;
        if (refused === 'APPLICATION_NOT_FOUND') setView({ kind: 'notFound' });
        else setView({ kind: 'failed', message: refused === 'RATE_LIMITED' ? message : undefined });
        return;
      }
      const { status, quote } = result.value;
      const cycle = quote === null ? undefined : cycleOf(quote.periodMonths);
      if (status.lane === 'legacy') setView(legacyView(status.state));
      else if (quote === null) setView({ kind: 'notFound' });
      else if (status.state !== 'pending_payment') setView({ kind: 'returnPage' });
      else if (cycle === undefined) setView({ kind: 'failed' });
      else setView({ kind: 'account', account: quote, cycle });
    });
    return () => {
      alive = false;
    };
  }, [applicationId, inThisBrowser, reads]);

  /** A refusal of the account's two calls: an application the server will not check out goes to the return page; else its line. */
  const refuse = (refusal: ApiRefusal, id: string) => {
    const paid = refusal.code === 'ACCOUNT_ALREADY_PAID' || refusal.code === 'CHECKOUT_ALREADY_PAID';
    if (paid || refusal.code === 'APPLICATION_STATE_INVALID') navigate(returnRoute(id, paid));
    else setFailure(refusal.code === 'RATE_LIMITED' ? refusal.message : t('publicOnboarding.resume.failed'));
  };

  // One call at a time (D-C4): the box and both buttons wait while one runs.
  const run = async (work: (id: string) => Promise<void>) => {
    if (applicationId === null) return;
    setBusy(true);
    setFailure('');
    try {
      await work(applicationId);
    } finally {
      setBusy(false);
    }
  };

  // The checkout's discount box (D-C5): a blank code clears the applied one, any other is previewed by P3 on the account as built.
  const applyCode = () =>
    run(async (id) => {
      setCodeError('');
      const typed = code.trim();
      if (typed === '') {
        setDiscount(null);
        return;
      }
      const answer = await fetchPerSeatQuote(id, typed);
      if (answer.ok) setDiscount(answer.value.discount);
      else if (answer.refusal.code === 'DISCOUNT_INVALID') setCodeError(answer.refusal.message);
      else refuse(answer.refusal, id);
    });

  // His gold button (P4): the applied code, the return route Stripe sends him back to, then Stripe Checkout.
  const startCheckout = () =>
    run(async (id) => {
      const returnTo = `${window.location.origin}${RETURN_ROUTE}`;
      const answer = await startPerSeatCheckout(id, discount === null ? undefined : discount.code, returnTo);
      if (answer.ok) window.location.assign(answer.value.checkoutUrl);
      else refuse(answer.refusal, id);
    });

  // The old lane's gold button (D3, self-stamps 2 and 20): the typed code rides the old view's body unpreviewed, and Stripe
  // sends him back to the old lane's `/?status=...`. That lane names a refused code or state by its message alone (HTTP_400),
  // shown as the old view showed it; every other refusal is answered as the account's.
  const startLegacy = () =>
    run(async (id) => {
      const typed = code.trim();
      const answer = await startLegacyCheckout(id, typed === '' ? undefined : typed);
      if (answer.ok) window.location.assign(answer.value.checkoutUrl);
      else if (answer.refusal.code === 'HTTP_400') setFailure(answer.refusal.message);
      else refuse(answer.refusal, id);
    });

  if (inThisBrowser) return <Navigate to={CHECKOUT_ROUTE} replace />;
  if (view.kind === 'returnPage' && applicationId !== null) return <Navigate to={returnRoute(applicationId, true)} replace />;

  const progress = publicJourney(draft.path, 'checkout');
  const gold = 'btn-gold inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl text-sm font-bold';
  const money = (n: number) => formatMxn(n, language);
  // The two pay views' shared pieces (self-stamp 22): the last call's refusal line and the code box.
  const failureLine =
    failure === '' ? null : (
      <p className="auth-err">
        <AlertTriangle size={12} />
        {failure}
      </p>
    );
  const codeInput = (
    <input
      value={code}
      onChange={(event) => setCode(event.target.value)}
      placeholder={t('settings.createOrganization.discountPlaceholder')}
      aria-label={t('settings.createOrganization.discountLabel')}
      disabled={busy}
    />
  );

  // The old lane's pay view (D3, self-stamps 21 and 23): his title, its line, the code box with no preview (Enter pays too) and
  // his gold button - no link to change the order, since /onboarding builds a per-seat application, never an old one.
  if (view.kind === 'legacy') {
    const legacyFoot = (
      <>
        {failureLine}
        <div className="org-float">
          <form
            className="org-code is-quiet org-code-foot"
            onSubmit={(event) => {
              event.preventDefault();
              startLegacy();
            }}
          >
            {codeInput}
          </form>
          <button type="button" className={gold} onClick={startLegacy} disabled={busy}>
            {t('settings.createOrganization.stripeCheckout')}
          </button>
        </div>
      </>
    );
    return (
      <OnboardingFrame step={progress.step} total={progress.total} band="card" foot={legacyFoot}>
        <div className="ob-flow-copy">
          <h1 className="ob-flow-title">{t('publicOnboarding.resume.title')}</h1>
          <p className="ob-flow-sub">{t('publicOnboarding.resume.legacySubtitle')}</p>
        </div>
      </OnboardingFrame>
    );
  }

  if (view.kind !== 'account') {
    let title = t('publicOnboarding.return.loading');
    let line = '';
    let action: React.ReactNode = null;
    if (view.kind === 'notFound') {
      title = t('publicOnboarding.return.notFound');
      action = (
        <a className={gold} href={LOGIN_URL}>
          {t('publicOnboarding.return.login')}
        </a>
      );
    } else if (view.kind === 'failed') {
      title = t('publicOnboarding.resume.title');
      line = view.message ?? t('publicOnboarding.resume.failed');
      action = (
        <button type="button" className={gold} onClick={() => setReads((n) => n + 1)}>
          {t('publicOnboarding.resume.retry')}
        </button>
      );
    }
    return (
      <OnboardingFrame step={progress.step} total={progress.total} band="card">
        <div className="ob-flow-copy">
          <h1 className="ob-flow-title">{title}</h1>
          {line === '' ? null : (
            <p className="auth-err">
              <AlertTriangle size={12} />
              {line}
            </p>
          )}
        </div>
        {action === null ? null : <div className="org-float">{action}</div>}
      </OnboardingFrame>
    );
  }

  const { account, cycle } = view;
  const prepaid = account.periodMonths > 1;
  const foot = (
    <>
      {failureLine}
      <div className="org-float">
        <Link className="ob-back" to={CHANGE_ROUTE}>
          {t('publicOnboarding.resume.change')}
        </Link>
        <form
          className="org-code is-quiet org-code-foot"
          onSubmit={(event) => {
            event.preventDefault();
            applyCode();
          }}
        >
          {codeInput}
          <button type="submit" disabled={busy}>
            {t('settings.createOrganization.discountApply')}
          </button>
          {codeError === '' ? null : (
            <p className="auth-err">
              <AlertTriangle size={12} />
              {codeError}
            </p>
          )}
        </form>
        <button type="button" className={gold} onClick={startCheckout} disabled={busy}>
          {t('settings.createOrganization.stripeCheckout')}
        </button>
      </div>
    </>
  );

  return (
    <OnboardingFrame step={progress.step} total={progress.total} band="card" foot={foot}>
      <div className="ob-flow-copy">
        <h1 className="ob-flow-title">{t('publicOnboarding.resume.title')}</h1>
        <p className="ob-flow-sub">{t('publicOnboarding.resume.subtitle')}</p>
      </div>
      <div className="org-float-price org-due">
        <b className="tabular-nums">{money(discount === null ? account.totalMxn : discount.totalAfterMxn)}</b>
        <span>{t(prepaid ? 'settings.createOrganization.dueNow' : 'settings.createOrganization.perMonth')}</span>
      </div>
      {discount === null ? null : (
        <div className="org-bill-row is-discount">
          <span>{`${t('settings.createOrganization.billDiscount')} \u00b7 ${discount.code}`}</span>
          <b className="tabular-nums">{`\u2212${money(discount.amountOffMxn)}`}</b>
        </div>
      )}
      <div className="org-bill">
        <section className="org-glass-card org-bill-card">
          <h3 className="org-bill-head">
            <Sparkles size={18} />
            {t(`settings.upgrade.billing.${cycle}`)}
          </h3>
          {account.lines.map((line) => (
            <div key={line.rowKey} className="org-bill-row">
              <span>{`${lineLabel(line, account.plan, t)} \u00b7 ${line.quantity} \u00d7 ${money(line.unitAmountMxn)}`}</span>
              <b className="tabular-nums">{money(line.amountMxn)}</b>
            </div>
          ))}
          <div className="org-bill-row is-total">
            <span>{t('publicOnboarding.resume.total')}</span>
            <b className="tabular-nums">{money(account.totalMxn)}</b>
          </div>
        </section>
      </div>
    </OnboardingFrame>
  );
};

export default OnboardingResumePage;
